export const NAMESPACES = {
  COMMON: 'common',
  USECASE: 'usecase',
  DATASET: 'dataset',
  // Value stays 'customAssessment' — it's the locize namespace id; renaming it
  // is a content-ops migration of the namespace itself, not a code change.
  GOVERNANCE_EVALUATION: 'customAssessment',
} as const;

export type Namespace = (typeof NAMESPACES)[keyof typeof NAMESPACES];

export function tenantNamespace(tenantId: string): string {
  return `tenant-${tenantId}`;
}

export interface CommonResources {
  actions: {
    save: string;
    cancel: string;
    delete: string;
    edit: string;
    add: string;
  };
}

export interface UsecaseResources {
  inventory: {
    title: string;
    addButton: string;
  };
  form: {
    title: string;
    nameLabel: string;
    businessOwnerLabel: string;
    euAiActRiskClassLabel: string;
    departmentsLabel: string;
    materialityLabel: string;
    descriptionLabel: string;
    purposeLabel: string;
    cancelButton: string;
    createButton: string;
    saveButton: string;
    selectRiskClassPlaceholder: string;
    selectDepartmentsPlaceholder: string;
    selectMaterialityPlaceholder: string;
    validation: {
      nameRequired: string;
      businessOwnerRequired: string;
      riskClassRequired: string;
      descriptionRequired: string;
      purposeRequired: string;
    };
  };
  toast: {
    createSuccess: string;
    createCustomFieldsError: string;
    updateSuccess: string;
    updateCustomFieldsError: string;
    deleteSuccess: string;
    idMissing: string;
  };
  details: {
    backToInventory: string;
    businessOwner: string;
    departments: string;
    businessCriticality: string;
    euAiActRiskClass: string;
    lastUpdated: string;
    description: string;
    purpose: string;
    viewMore: string;
    viewLess: string;
    editTitle: string;
    deleteTitle: string;
    tabs: {
      details: string;
      lifecycle: string;
      riskProfile: string;
    };
  };
  table: {
    columns: {
      useCase: string;
      models: string;
      euAiAct: string;
      lifecycle: string;
      materiality: string;
      assessmentStatus: string;
      businessOwner: string;
      departments: string;
      description: string;
      purpose: string;
      url: string;
    };
    manage: string;
    noResults: string;
    openMenu: string;
    searchPlaceholder: string;
    reset: string;
    backToDefault: string;
    saveFilters: string;
  };
  actions: {
    edit: string;
    manage: string;
    delete: string;
    assess: string;
    customAssessment: string;
    startEvaluation: string;
  };
  sidebar: {
    inventory: string;
  };
}

export interface DatasetResources {
  actions: {
    startAssessment: string;
  };
  sidebar: {
    inventory: string;
  };
  inventory: {
    title: string;
    addButton: string;
    backButton: string;
    createFromModelButton: string;
  };
  form: {
    nameLabel: string;
    datasetFileSuffix: string;
  };
  preview: {
    button: string;
    rows: string;
    columns: string;
    delimiterWarning: string;
    download: string;
    remove: string;
  };
  table: {
    columns: {
      name: string;
      evaluationData: string;
    };
  };
  summary: {
    nameLabel: string;
    untitledName: string;
    datasetLabel: string;
  };
  selectionStep: {
    title: string;
    description: string;
    hint: string;
    modelScopeNote: string;
    noPipelinesFound: string;
    addDataset: string;
    disabledTooltip: string;
  };
  assessment: {
    datasetLabel: string;
    viewDatasetPipeline: string;
  };
  recreateStep: {
    scratchDesc: string;
    recreateTitle: string;
    recreateDesc: string;
    noResults: string;
  };
  inputs: {
    uploadedDataset: string;
    recreateButton: string;
    recreateTooltip: string;
    prefillBanner: string;
    schemaFields: {
      title: string;
      downloadJson: string;
      viewAsTable: string;
      viewAsTags: string;
      hint: string;
      columnField: string;
      columnType: string;
      columnExactMatch: string;
      columnDescription: string;
    };
  };
  overview: {
    baselineDataset: string;
    perturbedDatasets: string;
    noPerturbedDatasets: string;
    noDatasetsSelected: string;
    baselineDatasetLabel: string;
    perturbedDatasetSingular: string;
    perturbedDatasetPlural: string;
    selectToDownload: string;
  };
}

export interface GovernanceEvaluationResources {
  sidebar: {
    label: string;
    logsLabel: string;
  };
  inventory: {
    title: string;
    backButton: string;
  };
  report: {
    header: string;
  };
  header: {
    reEvaluate: string;
  };
  details: {
    useCaseLabel: string;
    performedByLabel: string;
    questionnaireLabel: string;
    evaluatedAtLabel: string;
    tabs: {
      outputs: string;
      inputs: string;
    };
  };
  outputs: {
    manageRiskProfile: string;
    noResults: string;
    riskTitlePrefix: string;
    calculatedPrefix: string;
    inherentRisk: string;
  };
  riskProfile: {
    evaluationsCardTitle: string;
  };
}

declare module 'i18next' {
  interface CustomTypeOptions {
    defaultNS: 'common';
    resources: {
      common: CommonResources;
      usecase: UsecaseResources;
      dataset: DatasetResources;
      customAssessment: GovernanceEvaluationResources;
    };
  }
}
